<template>
  <ObservedForm
    ref="observer"
    :loading="loading"
    noerror
    @submit="searchByName"
  >
    <VRow ref="root" class="add-processing" no-gutters>
      <VCol
        v-if="demonstrators.length === 0"
        data-testid="demonstratorsVFlex"
        class="px-4 py-6 top-border text-right"
      >
        <TextFieldWithValidation
          v-model="searchName"
          :aria-label="$t('label.name')"
          :placeholder="$t('label.name')"
          rules="min:3"
          name="name"
          variant="filled"
          :hide-details-without-error="true"
          data-testid="demo-search-name"
          @focus="onFocus"
        />
        <SBtn
          :disabled="loading"
          class="self-align-end"
          color="secondary"
          type="submit"
          link-medium
          data-testid="searchByNameButton"
        >
          {{ $t('input.btn.search') }}
        </SBtn>
        <p
          v-if="noResults"
          ref="noMatches"
          class="mt-3 d-block text-center"
          tabindex="-1"
          data-testid="demo-no-results"
        >
          {{ $t('demonstrator.label.noMatchesName') }}
        </p>
      </VCol>
      <VCol v-if="demonstrators.length > 0">
        <DemonstratorList
          :demonstrators="demonstrators"
          @demo-selected="emits('demo-selected', $event)"
        />
        <VDivider class="my-0" />
        <VList
          data-testid="demonstratorsList"
          class="pa-0 text-left"
        >
          <VListItem class="px-4 py-0">
            <SBtn
              v-if="showMoreButton"
              :disabled="loading"
              data-testid="showMoreButton"
              class="text-uppercase font-weight-medium"
              color="secondary"
              link-medium
              @click="showMore"
            >
              {{ $t('input.btn.showMore') }}
            </SBtn>
            <template #append>
              <button
                type="button"
                class="icon-btn mr-n1"
                data-testid="reset-demo-name-search"
                :aria-label="$t('input.btn.newSearch')"
                @click="newSearch"
              >
                <SIcon :icon="icons.mdiAccountSearch" color="primary" size="24" decorative />
              </button>
            </template>
          </VListItem>
        </VList>
      </VCol>
    </VRow>
  </ObservedForm>
</template>

<script lang="ts" setup>
import { mdiAccountSearch } from '@mdi/js'
import type { Demonstrator } from '~/types/account'
import DemonstratorList from '~/components/account/demonstrator/DemonstratorList.vue'
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import { firstDemonstratorResult, focusSearchOutcome } from '~/utils/focus'

const props = withDefaults(defineProps<{
  nameEndpoint?: string
}>(), {
  nameEndpoint: 'demonstrator/by-name'
})

const emits = defineEmits<{
  (e: 'demo-selected', demonstrator: Demonstrator): void
}>()

const { loading, processAction } = useAction()

const searchName = ref('')
const demonstrators = ref<Demonstrator[]>([])
const icons = { mdiAccountSearch }

const showMoreButton = ref(true)
const page = ref(0)
const pageMax = 20
const noResults = ref(false)

const observer = useTemplateRef('observer')
const root = useTemplateRef('root')
const noMatches = useTemplateRef('noMatches')

// Land on the first result or the no-matches message, as nuxt2 does (PR 23563). AB#65420
const focusOutcome = () =>
  focusSearchOutcome(
    () => root.value?.$el as HTMLElement | undefined,
    finder => (demonstrators.value.length > 0 ? firstDemonstratorResult(finder) : noMatches.value)
  )

const searchByName = async () => {
  await processAction(async () => {
    if (searchName.value) {
      noResults.value = false
      const { totalPages, results } = await $api()<{ totalPages: number, results: Demonstrator[] }>(`${props.nameEndpoint}?name=${searchName.value}&page=0`)
      demonstrators.value = results as Demonstrator[]
      showMoreButton.value = totalPages > 1
      noResults.value = totalPages === 0
    }
  })
  await focusOutcome()
}

const searchByNameMore = async () => {
  const { totalPages, results } = await $api()<{ totalPages: number, results: Demonstrator[] }>(`${props.nameEndpoint}?name=${searchName.value}&page=${page.value}`)
  demonstrators.value.push(...results as Demonstrator[])
  showMoreButton.value = totalPages > (page.value + 1)
}

const onFocus = () => {
  if (noResults.value) {
    observer.value?.resetForm()
    noResults.value = false
    searchName.value = ''
  }
}

const newSearch = async () => {
  page.value = 0
  searchName.value = ''
  demonstrators.value = []
  // The reset button unmounts with the results; hand focus to the search input.
  await nextTick()
  const field = document.querySelector<HTMLElement>('[data-testid="demo-search-name"]')
  const input = field instanceof HTMLInputElement ? field : field?.querySelector('input')
  input?.focus()
}

const showMore = async () => {
  await processAction(async () => {
    if (page.value < pageMax) {
      page.value++
      await searchByNameMore()
      document.querySelector('.demo-list .v-list-item:last-child')?.scrollIntoView()
    }
  })
}
</script>

<style lang="scss" scoped>
.top-border {
  border-top: 1px solid rgb(var(--v-theme-border));
}
.background-grey {
  background-color: rgb(var(--v-theme-backgroundDiv));
}
</style>
